import React, { Fragment, useCallback } from 'react';
import { View } from 'react-native';
import { styled, useBreakpointIndex } from 'dripsy';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  Button,
  Line,
  ScreenErrorView,
  ScreenLoader,
  ScrollView,
  Separator,
} from '@components';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { CourseDescription } from './components/CourseDescription';
import { CourseHighlights } from './components/CourseHighlights';
import { CourseDetails } from './components/CourseDetails';
import { IS_WEB, parseApiError } from '@utils';
import { useCourseCollectionModel } from '@models/Courses/CourseCollectionModel';
import { AnalyticsService, ToastService } from '@services';
import {
  HubNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
  screens,
} from '@navigation';
import { Course } from '@models/Courses/CourseModel';
import { CourseIntroVideoPlayer } from './components/CourseIntroVideoPlayer';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const ScrollViewContainer = styled(ScrollView)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(insets: EdgeInsets) {
  return {
    flexGrow: 1,
    px: ['$6', '5%'],
    pb: insets.bottom,
  };
}

const CourseDetailsContainer = styled(View)({
  bg: '$background0',
  padding: [null, '$8'],
  borderRadius: [null, 16],
  shadowRadius: [null, 16],
  shadowColor: [null, '$shadow'],
  shadowOpacity: [null, 0.1],
  shadowOffset: IS_WEB ? { height: 3, width: 3 } : undefined,
  overflow: 'hidden',
});

const ContentContainer = styled(View)({
  flex: 2,
  mr: '$8',
});

const SideSectionContainer = styled(View)({
  flex: 1,
});

const DesktopContentWrapper = styled(View)({
  flexDirection: 'row',
});

const VideoContainer = styled(View)({
  borderRadius: 16,
  overflow: 'hidden',
  borderWidth: 1,
  borderColor: '$border100',
});

type NavigationPropType =
  HubNavigatorNavigationPropType<'CourseSummary'>;
type NavigationRoutePropType =
  HubNavigatorRoutePropType<'CourseSummary'>;

export const CourseSummaryScreen = observer(() => {
  const { params } = useRoute<NavigationRoutePropType>();
  const courses = useCourseCollectionModel();
  const item = courses.getById(params.id);
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const bIndex = useBreakpointIndex();
  const nav = useNavigation<NavigationPropType>();

  useNavOptionsSet({ title: item?.title }, [item?.title]);

  useAsyncInit(async () => {
    try {
      await courses.fetchById.run(params.id);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [params.id]);

  async function startCourse() {
    if (!item) {
      return;
    }

    if (!item.userStats?.isStarted) {
      try {
        await item.start.run();

        AnalyticsService.trackCourseStarted({
          courseId: item.id,
          courseTitle: item.title,
        });
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    nav.push(screens.Tutorial, {
      id: item.userStats?.currentVideoId ?? item.introVideo.id,
      courseId: item.id,
    });
  }

  const CourseDetailsSection = useCallback(
    () => (
      <CourseDetailsContainer>
        <VideoContainer>
          <CourseIntroVideoPlayer course={item as Course} />
        </VideoContainer>
        <Separator size="$8" />
        <CourseDetails course={item as Course} />
        <Separator size="$6" />
        <Line />
        <Separator size="$6" />
        <Button onPress={startCourse}>
          {item?.userStats?.isStarted
            ? t('courseSummary.continueCourse')
            : t('courseSummary.startCourse')}
        </Button>
      </CourseDetailsContainer>
    ),
    [item],
  );

  if (courses.fetchById.isError) {
    return <ScreenErrorView error={courses.fetchById.error} />;
  }

  if (!item || (courses.fetchById.inProgress && !item)) {
    return <ScreenLoader />;
  }

  return (
    <Container>
      <ScrollViewContainer
        contentContainerSx={contentContainerStyle(insets)}
      >
        {bIndex > 0 && IS_WEB ? (
          <DesktopContentWrapper>
            <ContentContainer>
              <CourseDescription course={item} />
              <Separator size="$9" />
              <CourseHighlights course={item} />
            </ContentContainer>
            <SideSectionContainer>
              <CourseDetailsSection />
            </SideSectionContainer>
          </DesktopContentWrapper>
        ) : (
          <Fragment>
            <CourseDescription course={item} />
            <Separator size="$8" />
            <CourseDetailsSection />
            <Separator size="$9" />
            <CourseHighlights course={item} />
            <Separator size="$8" />
          </Fragment>
        )}
      </ScrollViewContainer>
    </Container>
  );
});
